import React from 'react';
import { StepProps, Products, SizeQuantities, JerseyTeamSizes, PantsTeamSizes } from '../../../types';
import {
  TYPE_LABELS,
  getAllTeams,
  formatSizes,
  formatDate
} from '../../../utils/formHelpers';

// Type guard for jersey sizes (has youth/adult/coach)
const isJerseySizes = (sizes: unknown): sizes is JerseyTeamSizes => {
  if (!sizes || typeof sizes !== 'object') return false;
  return 'youth' in sizes && 'adult' in sizes && 'coach' in sizes;
};

// Type guard for pants sizes (has youth/adult)
const isPantsSizes = (sizes: unknown): sizes is PantsTeamSizes => {
  if (!sizes || typeof sizes !== 'object') return false;
  return 'youth' in sizes && 'adult' in sizes && !('coach' in sizes);
};

// Type guard for flat sizes
const isFlatSizes = (sizes: unknown): sizes is SizeQuantities => {
  if (!sizes || typeof sizes !== 'object') return false;
  // Flat sizes don't have youth/adult/coach properties
  return !('youth' in sizes) && !('adult' in sizes) && !('coach' in sizes);
};

// Calculate total from nested jersey sizes
const getJerseyTotal = (sizes: JerseyTeamSizes): number => {
  let total = 0;
  ['youth', 'adult', 'coach'].forEach(category => {
    const catSizes = sizes[category as keyof JerseyTeamSizes];
    if (catSizes) {
      Object.values(catSizes).forEach(qty => {
        total += parseInt(String(qty)) || 0;
      });
    }
  });
  return total;
};

// Calculate total from nested pants sizes
const getPantsTotal = (sizes: PantsTeamSizes): number => {
  let total = 0;
  ['youth', 'adult'].forEach(category => {
    const catSizes = sizes[category as keyof PantsTeamSizes];
    if (catSizes) {
      Object.values(catSizes).forEach(qty => {
        total += parseInt(String(qty)) || 0;
      });
    }
  });
  return total;
};

// Calculate total from flat sizes
const getFlatTotal = (sizes: SizeQuantities): number => {
  return Object.values(sizes).reduce((sum, qty) => sum + (parseInt(String(qty)) || 0), 0);
};

// Format nested jersey sizes for display
const formatJerseySizes = (sizes: JerseyTeamSizes): string => {
  const parts: string[] = [];

  // Youth sizes
  const youthSizes = Object.entries(sizes.youth || {})
    .filter(([_, qty]) => qty > 0)
    .map(([size, qty]) => `${size}: ${qty}`);
  if (youthSizes.length > 0) {
    parts.push(`Youth: ${youthSizes.join(', ')}`);
  }

  // Adult sizes
  const adultSizes = Object.entries(sizes.adult || {})
    .filter(([_, qty]) => qty > 0)
    .map(([size, qty]) => `${size}: ${qty}`);
  if (adultSizes.length > 0) {
    parts.push(`Adult: ${adultSizes.join(', ')}`);
  }

  // Coach sizes
  const coachSizes = Object.entries(sizes.coach || {})
    .filter(([_, qty]) => qty > 0)
    .map(([size, qty]) => `${size}: ${qty}`);
  if (coachSizes.length > 0) {
    parts.push(`Coach: ${coachSizes.join(', ')}`);
  }

  return parts.join(' | ');
};

// Format nested pants sizes for display
const formatPantsSizes = (sizes: PantsTeamSizes): string => {
  const parts: string[] = [];

  // Youth sizes
  const youthSizes = Object.entries(sizes.youth || {})
    .filter(([_, qty]) => qty > 0)
    .map(([size, qty]) => `${size}: ${qty}`);
  if (youthSizes.length > 0) {
    parts.push(`Youth: ${youthSizes.join(', ')}`);
  }

  // Adult sizes
  const adultSizes = Object.entries(sizes.adult || {})
    .filter(([_, qty]) => qty > 0)
    .map(([size, qty]) => `${size}: ${qty}`);
  if (adultSizes.length > 0) {
    parts.push(`Adult: ${adultSizes.join(', ')}`);
  }

  return parts.join(' | ');
};

const ReviewStep: React.FC<StepProps> = ({ formData }) => {
  const { divisions, products } = formData;

  const totalTeams = divisions.reduce((sum, div) => sum + div.teams.length, 0);
  const allTeams = getAllTeams(divisions);

  // Calculate team total based on product type
  const getTeamProductTotal = (productKey: keyof Products, teamKey: string): number => {
    const sizes = products[productKey].teamSizes?.[teamKey];
    if (!sizes) return 0;

    if (productKey === 'jerseys' && isJerseySizes(sizes)) {
      return getJerseyTotal(sizes);
    }
    if (productKey === 'pants' && isPantsSizes(sizes)) {
      return getPantsTotal(sizes);
    }
    if (isFlatSizes(sizes)) {
      return getFlatTotal(sizes);
    }
    return 0;
  };

  // Calculate total for a product across all teams
  const getProductTotal = (productKey: keyof Products): number => {
    const teamSizes = products[productKey].teamSizes;
    if (!teamSizes) return 0;

    let total = 0;
    Object.keys(teamSizes).forEach(teamKey => {
      total += getTeamProductTotal(productKey, teamKey);
    });
    return total;
  };

  const totalItems = getProductTotal('jerseys') + getProductTotal('pants') +
    getProductTotal('belts') + getProductTotal('socks') + getProductTotal('caps');

  // Check if product has any items
  const hasItems = (productKey: keyof Products): boolean => getProductTotal(productKey) > 0;

  // Format sizes for display based on product type
  const formatTeamSizes = (productKey: keyof Products, teamKey: string): string => {
    const sizes = products[productKey].teamSizes?.[teamKey];
    if (!sizes) return '';

    if (productKey === 'jerseys' && isJerseySizes(sizes)) {
      return formatJerseySizes(sizes);
    }
    if (productKey === 'pants' && isPantsSizes(sizes)) {
      return formatPantsSizes(sizes);
    }
    if (isFlatSizes(sizes)) {
      return formatSizes(sizes) || '';
    }
    return '';
  };

  return (
    <div className="step-content">
      <div className="step-section">
        <h3 className="step-section-title">Review Your Order Request</h3>
        <p className="step-section-description">
          Please review the information below before submitting. You can go back to make changes.
        </p>

        <div className="review-section">
          <h4 className="review-section-title">Contact Information</h4>
          <div className="review-grid">
            <div className="review-item">
              <label>League Name</label>
              <p>{formData.leagueName || 'Not provided'}</p>
            </div>
            <div className="review-item">
              <label>Contact Name</label>
              <p>{formData.contactName || 'Not provided'}</p>
            </div>
            <div className="review-item">
              <label>Email</label>
              <p>{formData.contactEmail || 'Not provided'}</p>
            </div>
            <div className="review-item">
              <label>Phone</label>
              <p>{formData.contactPhone || 'Not provided'}</p>
            </div>
          </div>
        </div>

        <div className="review-section">
          <h4 className="review-section-title">Order Details</h4>
          <div className="review-grid">
            <div className="review-item">
              <label>Season</label>
              <p>{formData.season || 'Not specified'}</p>
            </div>
            <div className="review-item">
              <label>Requested Delivery</label>
              <p>{formatDate(formData.requestedDate)}</p>
            </div>
          </div>
          {formData.notes && (
            <div className="review-item" style={{ marginTop: '16px' }}>
              <label>Notes</label>
              <p style={{ whiteSpace: 'pre-wrap' }}>{formData.notes}</p>
            </div>
          )}
        </div>

        <div className="review-section">
          <h4 className="review-section-title">
            Teams
            <span className="review-badge blue">{totalTeams} total</span>
          </h4>
          {divisions.map((division, i) => (
            <div key={i} style={{ marginBottom: i < divisions.length - 1 ? '16px' : 0 }}>
              <strong>{division.name || `Division ${i + 1}`}</strong>
              <div className="review-teams-list">
                {division.teams.map((team, j) => (
                  <span key={j} className="review-team-badge">
                    {team.name || `Team ${j + 1}`}
                    {team.color && ` (${team.color})`}
                    {team.sponsorName && ` - Sponsor: ${team.sponsorName}`}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="review-section">
          <h4 className="review-section-title">
            Products
            <span className="review-badge green">{totalItems} items</span>
          </h4>

          {totalItems === 0 ? (
            <p className="review-no-items">No products configured yet.</p>
          ) : (
            <div className="review-products-detail">
              {/* Jerseys */}
              {hasItems('jerseys') && (
                <div className="review-product-item">
                  <div className="review-product-header">
                    <span className="review-product-name">Jerseys</span>
                    <span className="review-product-type">{TYPE_LABELS[products.jerseys.type || '']}</span>
                    {products.jerseys.sku && <span className="review-product-sku">SKU: {products.jerseys.sku}</span>}
                    <span className="review-product-qty">{getProductTotal('jerseys')} items</span>
                  </div>
                  <div className="review-team-breakdown">
                    {allTeams.map(team => {
                      const teamTotal = getTeamProductTotal('jerseys', team.key);
                      if (teamTotal === 0) return null;
                      return (
                        <div key={team.key} className="review-team-row">
                          <span className="review-team-name">
                            {team.teamName}
                            <span className="review-team-division-label">({team.divisionName})</span>
                          </span>
                          <span className="review-team-sizes">{formatTeamSizes('jerseys', team.key)}</span>
                        </div>
                      );
                    })}
                  </div>
                  {products.jerseys.logo && (
                    <div className="review-product-logo">
                      <img src={products.jerseys.logo.data} alt="Jersey logo" className="review-logo-image" />
                      <span className="review-logo-label">Logo: {products.jerseys.logo.name}</span>
                    </div>
                  )}
                  {products.jerseys.notes && (
                    <div className="review-product-notes">
                      <em>Notes: {products.jerseys.notes}</em>
                    </div>
                  )}
                </div>
              )}

              {/* Pants */}
              {hasItems('pants') && (
                <div className="review-product-item">
                  <div className="review-product-header">
                    <span className="review-product-name">Pants</span>
                    <span className="review-product-type">{TYPE_LABELS[products.pants.type || '']}</span>
                    {products.pants.sku && <span className="review-product-sku">SKU: {products.pants.sku}</span>}
                    <span className="review-product-qty">{getProductTotal('pants')} items</span>
                  </div>
                  <div className="review-team-breakdown">
                    {allTeams.map(team => {
                      const teamTotal = getTeamProductTotal('pants', team.key);
                      if (teamTotal === 0) return null;
                      return (
                        <div key={team.key} className="review-team-row">
                          <span className="review-team-name">
                            {team.teamName}
                            <span className="review-team-division-label">({team.divisionName})</span>
                          </span>
                          <span className="review-team-sizes">{formatTeamSizes('pants', team.key)}</span>
                        </div>
                      );
                    })}
                  </div>
                  {products.pants.notes && (
                    <div className="review-product-notes">
                      <em>Notes: {products.pants.notes}</em>
                    </div>
                  )}
                </div>
              )}

              {/* Belts */}
              {hasItems('belts') && (
                <div className="review-product-item">
                  <div className="review-product-header">
                    <span className="review-product-name">Belts</span>
                    <span className="review-product-qty">{getProductTotal('belts')} items</span>
                  </div>
                  <div className="review-team-breakdown">
                    {allTeams.map(team => {
                      const teamTotal = getTeamProductTotal('belts', team.key);
                      if (teamTotal === 0) return null;
                      return (
                        <div key={team.key} className="review-team-row">
                          <span className="review-team-name">
                            {team.teamName}
                            <span className="review-team-division-label">({team.divisionName})</span>
                          </span>
                          <span className="review-team-sizes">{formatTeamSizes('belts', team.key)}</span>
                        </div>
                      );
                    })}
                  </div>
                  {products.belts.notes && (
                    <div className="review-product-notes">
                      <em>Notes: {products.belts.notes}</em>
                    </div>
                  )}
                </div>
              )}

              {/* Socks */}
              {hasItems('socks') && (
                <div className="review-product-item">
                  <div className="review-product-header">
                    <span className="review-product-name">Socks</span>
                    <span className="review-product-type">{TYPE_LABELS[products.socks.type || '']}</span>
                    <span className="review-product-qty">{getProductTotal('socks')} items</span>
                  </div>
                  <div className="review-team-breakdown">
                    {allTeams.map(team => {
                      const teamTotal = getTeamProductTotal('socks', team.key);
                      if (teamTotal === 0) return null;
                      return (
                        <div key={team.key} className="review-team-row">
                          <span className="review-team-name">
                            {team.teamName}
                            <span className="review-team-division-label">({team.divisionName})</span>
                          </span>
                          <span className="review-team-sizes">{formatTeamSizes('socks', team.key)}</span>
                        </div>
                      );
                    })}
                  </div>
                  {products.socks.notes && (
                    <div className="review-product-notes">
                      <em>Notes: {products.socks.notes}</em>
                    </div>
                  )}
                </div>
              )}

              {/* Caps */}
              {hasItems('caps') && (
                <div className="review-product-item">
                  <div className="review-product-header">
                    <span className="review-product-name">Caps</span>
                    <span className="review-product-type">{TYPE_LABELS[products.caps.type || '']}</span>
                    {products.caps.sku && <span className="review-product-sku">SKU: {products.caps.sku}</span>}
                    <span className="review-product-qty">{getProductTotal('caps')} items</span>
                  </div>
                  <div className="review-team-breakdown">
                    {allTeams.map(team => {
                      const teamTotal = getTeamProductTotal('caps', team.key);
                      if (teamTotal === 0) return null;
                      return (
                        <div key={team.key} className="review-team-row">
                          <span className="review-team-name">
                            {team.teamName}
                            <span className="review-team-division-label">({team.divisionName})</span>
                          </span>
                          <span className="review-team-sizes">{formatTeamSizes('caps', team.key)}</span>
                        </div>
                      );
                    })}
                  </div>
                  {products.caps.logo && (
                    <div className="review-product-logo">
                      <img src={products.caps.logo.data} alt="Cap logo" className="review-logo-image" />
                      <span className="review-logo-label">Logo: {products.caps.logo.name}</span>
                    </div>
                  )}
                  {products.caps.notes && (
                    <div className="review-product-notes">
                      <em>Notes: {products.caps.notes}</em>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="alert alert-info" style={{ marginTop: '24px' }}>
          <strong>What happens next?</strong>
          <p style={{ marginTop: '8px' }}>
            After you submit, our team will review your request and contact you within 1-2 business days
            to discuss pricing, sizes, and any custom details. No charges are applied until you approve
            the final order.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;
